import { FC } from 'react';
import styled from '@emotion/styled';
import Rating from '@comp/Rating/Rating';
import Paper from '@base/Paper/Paper';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Image from '@base/Image';
import dateHelper from '@utils/dates';
import textHelper from '@utils/textHelper';
import H3 from '@base/H3/H3';
import { Spacings, Fonts, breakpoints, mediaQuery } from '@styles/index';
import CardCarousel from './CardCarousel';

const quoteImage = 'quote.svg';

const SecWrapper = styled.section`
  position: relative;
  width: 1376px;
  margin: 160px auto 100px;
  padding-bottom: 60px;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin: 128px auto 60px;
  }
  ${mediaQuery.uptoMobile} {
    margin: 88px 0 48px;
    padding: 0 0 40px;
    width: 100%;
  }
`;

const Title = styled(H3)`
  font-size: 40px;
  text-align: center;
  margin-bottom: 40px;
  ${mediaQuery.uptoMobile} {
    font-size: 28px;
    padding: 0 16px;
  }
`;

const CardContainer = styled(Paper)`
  width: 848px;
  height: 274px;
  margin-right: ${Spacings.SPACING_6B};
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  justify-content: flex-end;
  padding-left: 96px;
  padding-right: 96px;
  padding-bottom: 40px;
  z-index: -2;
  ${mediaQuery.uptoTablet} {
    width: 728px;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: 0;
    padding: 16px;
    height: 300px;
    max-width: 296px;
    min-width: 296px;
    justify-content: flex-start;
    margin-right: 16px;
  }
`;

const QuoteImageContainer = styled.div`
  position: absolute;
  top: 30px;
  left: 60px;
  z-index: -1;
  img {
    height: 60px;
    width: 60px;
  }
  ${mediaQuery.uptoMobile} {
    top: 16px;
    left: 16px;

    img {
      z-index: 0;
      height: 40px;
      width: 35px;
    }
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    margin-top: 24px;
  }
`;

const ReviewTextWrapper = styled.div`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    flex-grow: 1;
  }
`;

type ReviewTextProps = {
  lineClamp: number;
};

const ReviewText = styled(H5)<ReviewTextProps>`
  line-height: 150%;
  ${mediaQuery.uptoMobile} {
    margin-top: 24px;
  }
`;

const MetaRow = styled(Row)`
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column-reverse;
    justify-content: flex-end;
  }
`;

type InfoProps = {
  $mt: string;
};

const Info = styled(P1)<InfoProps>`
  font-size: ${Fonts.H5};
  line-height: 150%;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${({ $mt }) => $mt};
    font-size: 12px;
  }
`;

const RatingContainer = styled(Column)`
  align-items: flex-end;
  ${mediaQuery.uptoMobile} {
    align-items: flex-start;
    margin-top: 16px;
  }
`;

const ReviewerName = styled(H4)`
  ${mediaQuery.uptoMobile} {
    font-size: 16px;
  }
`;

type UserType = {
  firstName: string;
  lastName: string;
  learnerMinutes: number;
};

type CardProps = {
  review: {
    user: UserType;
    others: string;
    createdAt: Date;
    choice: string;
  };
  goalName?: string;
  index: number;
};

const Card: FC<CardProps> = ({ review, goalName, index }) => {
  const { user, others, createdAt, choice } = review;
  const { firstName, lastName, learnerMinutes } = user;
  const reviewerName = `${firstName} ${lastName}`;
  return (
    <CardContainer className={index === 0 ? 'first' : ''}>
      <QuoteImageContainer>
        <Image src={quoteImage} alt="Quote" height={60} width={60} />
      </QuoteImageContainer>
      <ReviewTextWrapper>
        <ReviewText lineClamp={3}>{others}</ReviewText>
      </ReviewTextWrapper>
      <MetaRow>
        <Column>
          <ReviewerName>{reviewerName}</ReviewerName>
          <Info $mt="2px">
            {`${textHelper.commas(learnerMinutes)} learning minutes • ${
              goalName || ''
            }`}
          </Info>
        </Column>
        <RatingContainer>
          <Rating value={parseFloat(choice)} isReadOnly />
          <Info $mt="8px">{dateHelper.timeAgoSinceDate(createdAt)}</Info>
        </RatingContainer>
      </MetaRow>
    </CardContainer>
  );
};

type ReviewsSectionTypes = {
  block: {
    data: any[];
    label?: string;
  };
  goalDetails: {
    name: string;
  };
  href?: string;
  as?: string;
};

const ReviewsSection: FC<ReviewsSectionTypes> = ({
  block,
  goalDetails = {},
  href,
  as
}) => {
  const { data, label } = block;
  const { name } = goalDetails;

  return (
    <SecWrapper>
      <Title>What our learners say about Plus</Title>
      <CardCarousel label={label} step={1} href={href} as={as} type="dummy">
        {data.map((review, index) => (
          <Card
            key={String(index)}
            review={review}
            goalName={name}
            index={index}
          />
        ))}
      </CardCarousel>
    </SecWrapper>
  );
};

export default ReviewsSection;
